/* eslint-disable max-lines-per-function */
import {
   AppointmentDetails,
   AppointmentDetailsQuery,
   AppointmentPractitioner,
   PractitionerAbleToCompleteAppointmentQuery,
} from '@dreryk/egabinet-appointment-frontend-api';
import {
   HealthcareProviderLocation,
   HealthcareProviderLocationQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { CurrentHealthcareProviderPaymentsConfigurationEnabledQuery } from '@dreryk/egabinet-online-payments-integration-frontend-api';

import { isDemo } from '@dreryk/demo-variant-frontend';
import { useCqrsFind, useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { appointmentsTranslationKeys } from '../../../../translations';

type AppointmentUpdateFormLoaderRenderProps = {
   appointmentDetails: AppointmentDetails;
   practitioners: AppointmentPractitioner[];
   healthcareProviderLocations: HealthcareProviderLocation[];
   patient: PatientSimple;
   onlinePaymentsIntegrationExists: boolean;
};

type AppointmentUpdateFormLoaderRenderer = (result: AppointmentUpdateFormLoaderRenderProps) => JSX.Element;

type AppointmentUpdateFormLoaderProps = {
   readonly appointmentId: number;
   readonly children: AppointmentUpdateFormLoaderRenderer;
};

export const AppointmentUpdateFormLoader = (props: AppointmentUpdateFormLoaderProps): JSX.Element => {
   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearch<AppointmentPractitioner>(() => new PractitionerAbleToCompleteAppointmentQuery());

   const {
      data: healthcareProviderLocations,
      loading: healthcareProviderLocationsLoading,
      error: healthcareProviderLocationsError,
   } = useCqrsSearch<HealthcareProviderLocation>(() => new HealthcareProviderLocationQuery());

   const {
      data: appointmentDetails,
      loading: appointmentDetailsLoading,
      error: appointmentDetailsError,
   } = useCqrsGet<AppointmentDetails>(() => {
      const query = new AppointmentDetailsQuery();
      query.appointmentId = AppointmentId.of(props.appointmentId);
      return query;
   });

   const { data: patient, loading: patientLoading, error: patientError } = useCqrsGet<PatientSimple>(
      () => {
         const query = new PatientSimpleQuery();
         if (appointmentDetails) {
            query.id = Slot.of(appointmentDetails.patient.id);
         }
         return query;
      },
      {
         enabled: Boolean(appointmentDetails),
      }
   );

   const {
      data: onlinePaymentsIntegrationExists,
      loading: onlinePaymentsIntegrationExistsLoading,
      error: onlinePaymentsIntegrationExistsError,
   } = useCqrsFind(() => new CurrentHealthcareProviderPaymentsConfigurationEnabledQuery(), { enabled: !isDemo() });

   if (
      practitionersLoading ||
      healthcareProviderLocationsLoading ||
      appointmentDetailsLoading ||
      patientLoading ||
      onlinePaymentsIntegrationExistsLoading
   ) {
      return <LoaderSpinner />;
   }

   if (
      practitionersError ||
      healthcareProviderLocationsError ||
      appointmentDetailsError ||
      patientError ||
      onlinePaymentsIntegrationExistsError
   ) {
      return (
         <Message
            testId={'appointmentDetailsFetchError'}
            type={MessageType.NEGATIVE}
            label={appointmentsTranslationKeys.update.form.errors.fetchData}
            displayIndicator
         />
      );
   }

   return props.children({
      appointmentDetails: appointmentDetails,
      practitioners: practitioners,
      healthcareProviderLocations: healthcareProviderLocations,
      patient: patient,
      onlinePaymentsIntegrationExists: Boolean(onlinePaymentsIntegrationExists) ?? false,
   });
};
